// CSS the loader owns: the positioning frame, the launcher, the empty panel
// frame the React panel renders into, and the resize handle. The panel's own
// stylesheet (Tailwind, compiled into widget-panel.js) arrives with the panel.
// Colours come from the theme tokens `palette.ts` injects; nothing here is a
// hard-coded colour except black/white alpha for shadows.

export const LOADER_STYLES = /* css */ `
:host {
  /* No \`contain\` here: on this inline host it does nothing in the browser,
     but Floating UI reads it and positions popovers against the host. */
  all: initial;
}

.se-root {
  position: fixed;
  /* Width is set per-state below: shrunk to the launcher while collapsed
     (otherwise the panel-wide box blocks clicks on the host page next to the
     launcher), full panel width while open. The .se-pos-* classes pin the
     launcher to one of eight anchors. */
  display: flex;
  flex-direction: column;
  z-index: 2147483000;
  color: var(--foreground);
  font: 15px/1.47 var(--se-font);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color-scheme: light;
}
.se-root[data-theme="dark"] { color-scheme: dark; }
@media (prefers-color-scheme: dark) {
  .se-root[data-theme="auto"] { color-scheme: dark; }
}

.se-root *, .se-root *::before, .se-root *::after { box-sizing: border-box; }

.se-root.is-collapsed .se-panel { display: none; }
.se-root:not(.is-collapsed) .se-launcher { display: none; }

.se-root.is-collapsed {
  width: max-content;
  max-width: calc(100vw - 1rem);
  /* Click-through: while collapsed only the launcher takes pointer events. */
  pointer-events: none;
}
.se-root:not(.is-collapsed) {
  width: min(var(--se-panel-width), calc(100vw - 2rem));
}
.se-root .se-launcher,
.se-root:not(.is-collapsed) .se-panel { pointer-events: auto; }

/* Inside a host element (mount({ target })) rather than the viewport. */
.se-root.is-contained { position: absolute; }

.se-root.se-pos-bottom-right { bottom: var(--se-offset-y); right: var(--se-offset-x); align-items: flex-end; }
.se-root.se-pos-bottom-left { bottom: var(--se-offset-y); left: var(--se-offset-x); align-items: flex-start; }
.se-root.se-pos-bottom-center { bottom: var(--se-offset-y); left: 50%; transform: translateX(-50%); align-items: center; }
.se-root.se-pos-top-right { top: var(--se-offset-y); right: var(--se-offset-x); align-items: flex-end; }
.se-root.se-pos-top-left { top: var(--se-offset-y); left: var(--se-offset-x); align-items: flex-start; }
.se-root.se-pos-top-center { top: var(--se-offset-y); left: 50%; transform: translateX(-50%); align-items: center; }
.se-root.se-pos-center-left { top: 50%; left: var(--se-offset-x); transform: translateY(-50%); align-items: flex-start; }
.se-root.se-pos-center-right { top: 50%; right: var(--se-offset-x); transform: translateY(-50%); align-items: flex-end; }

.se-root.is-draggable .se-launcher { cursor: grab; touch-action: none; }
.se-root.is-draggable .se-launcher.is-dragging { cursor: grabbing; }
.se-root.is-dragging { user-select: none; }

.se-root.is-custom-position.se-stack-up:not(.is-collapsed) {
  flex-direction: column-reverse;
  justify-content: flex-end;
}
.se-root.is-custom-position.se-anchor-bl { align-items: flex-start; }
.se-root.is-custom-position.se-anchor-br { align-items: flex-end; }

/* Launcher. */
.se-launcher {
  height: var(--se-launcher-size);
  min-width: var(--se-launcher-size);
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid rgb(255 255 255 / 0.1);
  border-radius: 999px;
  background: var(--primary);
  color: var(--primary-foreground);
  box-shadow: 0 12px 28px rgb(0 0 0 / 0.2), 0 1px 0 rgb(255 255 255 / 0.18) inset;
  cursor: pointer;
  font: 600 15px/1 var(--se-font);
  transition: transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
}
.se-launcher.is-pill { padding: 0 1.25rem 0 1rem; }
.se-launcher svg { width: 45%; height: 45%; max-width: 28px; max-height: 28px; }
.se-launcher.is-pill svg { width: 22px; height: 22px; }
.se-launcher img {
  width: calc(var(--se-launcher-size) - 8px);
  height: calc(var(--se-launcher-size) - 8px);
  border-radius: 999px;
  object-fit: cover;
}
.se-launcher.is-pill img { width: 30px; height: 30px; margin-left: -0.35rem; }
.se-launcher:hover {
  background: var(--primary-hover);
  box-shadow: 0 16px 34px rgb(0 0 0 / 0.24), 0 0 0 4px var(--primary-glow), 0 1px 0 rgb(255 255 255 / 0.18) inset;
}
.se-launcher:active { transform: translateY(1px); }
.se-launcher:focus-visible { outline: 2px solid var(--ring); outline-offset: 3px; }

/* The panel frame. React renders inside it once widget-panel.js loads. */
.se-panel {
  position: relative;
  width: 100%;
  height: min(var(--se-panel-height), 85vh);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) + 0.25rem);
  background: var(--background);
  color: var(--foreground);
  box-shadow: 0 18px 50px var(--shadow-color), 0 2px 6px rgb(0 0 0 / 0.04);
  overflow: hidden;
}
.se-panel.is-resizing { user-select: none; }
.se-panel-body {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Popovers and hover cards (portalled here) stack above the panel. */
.se-portal { position: fixed; top: 0; left: 0; z-index: 2147483001; }
.se-panel-loading {
  margin: auto;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  animation: se-spin 800ms linear infinite;
}
@keyframes se-spin { to { transform: rotate(360deg); } }

/* Phones: the open panel takes the whole screen. */
@media (max-width: 480px) {
  .se-root:not(.is-collapsed):not(.is-contained) {
    inset: 0 !important;
    width: 100% !important;
    transform: none !important;
  }
  .se-root:not(.is-collapsed):not(.is-contained) .se-panel {
    height: 100% !important;
    width: 100% !important;
    border-radius: 0;
    border: 0;
  }
  .se-root:not(.is-contained) .se-resize-handle { display: none; }
}

/* Resize handle: a soft corner vignette, brand-tinted while in use. */
.se-resize-handle {
  position: absolute;
  width: 22px;
  height: 22px;
  z-index: 30;
  touch-action: none;
  transition: background 200ms ease;
  --se-handle: rgb(0 0 0 / 0.14);
}
.se-resize-handle:hover, .se-panel.is-resizing .se-resize-handle { --se-handle: var(--primary-glow); }
.se-resize-handle.is-top-left { top: 0; left: 0; cursor: nwse-resize; background: radial-gradient(circle at top left, var(--se-handle), transparent 75%); }
.se-resize-handle.is-top-right { top: 0; right: 0; cursor: nesw-resize; background: radial-gradient(circle at top right, var(--se-handle), transparent 75%); }
.se-resize-handle.is-bottom-left { bottom: 0; left: 0; cursor: nesw-resize; background: radial-gradient(circle at bottom left, var(--se-handle), transparent 75%); }
.se-resize-handle.is-bottom-right { bottom: 0; right: 0; cursor: nwse-resize; background: radial-gradient(circle at bottom right, var(--se-handle), transparent 75%); }

/* Studio preview: the highlighted part, and clickable parts. */
.se-root.is-preview [data-se-part] { cursor: pointer; }
.se-root [data-se-highlight] {
  outline: 2px solid var(--primary) !important;
  outline-offset: 2px;
  border-radius: calc(var(--radius) * 0.6);
}

@media (prefers-reduced-motion: reduce) {
  .se-root *, .se-root *::before, .se-root *::after {
    animation-duration: 1ms !important;
    transition-duration: 1ms !important;
  }
}
`
